// =============================================================================
// Provider Dashboard Page — KPI cards + recent orders for provider users
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Card, Badge, Spinner } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';

interface DashboardData {
  kpis: {
    pending: number;
    inProduction: number;
    produced: number;
    readyToShip: number;
    shippedToday: number;
    delayed: number;
    totalOrders: number;
  };
  recentOrders: {
    id: string;
    orderNumber: string;
    status: string;
    total: string;
    currency: string;
    itemCount: number;
    createdAt: string;
  }[];
}

const STATUS_VARIANT: Record<string, 'primary' | 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  NEW: 'primary',
  DESIGN_PENDING: 'info',
  IN_PRODUCTION: 'warning',
  PRODUCTION_DONE: 'info',
  READY_TO_SHIP: 'info',
  SHIPPED: 'success',
  DELIVERED: 'success',
  CANCELLED: 'danger',
};

export function ProviderDashboardPage() {
  const api = useApiClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'provider-dashboard'],
    queryFn: () =>
      api.get<{ data: DashboardData }>('/api/v1/admin/orders/provider-dashboard'),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  const dashboard = data?.data;
  if (!dashboard) {
    return (
      <div className="py-12 text-center text-sm text-text-tertiary">
        Dashboard data not available.
      </div>
    );
  }

  const kpis = [
    { label: 'Pending', value: dashboard.kpis.pending, color: 'text-blue-600' },
    { label: 'In Production', value: dashboard.kpis.inProduction, color: 'text-amber-600' },
    { label: 'Produced', value: dashboard.kpis.produced, color: 'text-teal-600' },
    { label: 'Ready to Ship', value: dashboard.kpis.readyToShip, color: 'text-cyan-600' },
    { label: 'Shipped Today', value: dashboard.kpis.shippedToday, color: 'text-green-600' },
    { label: 'Delayed', value: dashboard.kpis.delayed, color: 'text-red-600' },
    { label: 'Total Orders', value: dashboard.kpis.totalOrders, color: 'text-text-primary' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-text-primary">Provider Dashboard</h1>
        <p className="text-sm text-text-secondary mt-1">
          Overview of your assigned orders
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((kpi) => (
          <Card key={kpi.label} padding="md">
            <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
              {kpi.label}
            </p>
            <p className={`text-2xl font-bold mt-1 ${kpi.color}`}>{kpi.value}</p>
          </Card>
        ))}
      </div>

      {/* Recent Orders */}
      <Card padding="none">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-text-primary">Recent Orders</h2>
        </div>

        {dashboard.recentOrders.length === 0 ? (
          <div className="py-12 text-center text-sm text-text-tertiary">
            No orders assigned yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Order
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Items
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Total
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {dashboard.recentOrders.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-surface-secondary/50 cursor-pointer"
                    onClick={() => navigate(`/orders/${order.id}`)}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-text-primary">
                      {order.orderNumber}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={STATUS_VARIANT[order.status] ?? 'default'}
                        size="sm"
                      >
                        {order.status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {order.itemCount}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-primary text-right font-medium">
                      ${order.total}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary text-right">
                      {new Date(order.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
